import styled, { css } from 'styled-components';
import { Inline, Stack } from '@superdispatch/ui';
import { ThumbUp } from '@material-ui/icons';
import { Link, Typography } from '@material-ui/core';
import { chooseUTMSource } from 'shared/helpers/Analytics';
import { formatNumber } from 'shared/helpers/IntlHelpers';
import { EditRating } from 'shared/modules/ratings/EditRating';
import { Button } from '@superdispatch/ui-lab';
import { ShipperProfileDTO } from 'shared/modules/shipper/ShipperProfileDTO';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { useLatestShipperRating } from 'shared/modules/ratings/data/ShipperRatingsAPI';
import { useIsRatingAvailable } from 'shared/modules/ratings/ShipperRatingUtils';
import { ShipperRatingAvailableTimeLeft } from 'shared/modules/ratings/ShipperRatingAvailableTimeLeft';

const CollapsedRatingContainer = css`
  align-items: flex-end;
  flex-direction: column;
  gap: 8px;
`;

const RatingContainer = styled.div<{ shouldCollapse: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;

  & > div {
    width: auto;
  }

  ${({ shouldCollapse }) => shouldCollapse && CollapsedRatingContainer}

  ${({ theme }) => theme.breakpoints.down('xs')} {
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }
`;

interface ShipperProfileHeaderRateProps {
  shipperProfile: ShipperProfileDTO;
  onClickRate: () => void;
  onClickDeleteRating: () => void;
}

export function ShipperProfileHeaderRating({
  shipperProfile,
  onClickRate,
  onClickDeleteRating,
}: ShipperProfileHeaderRateProps) {
  const [{ tab }, setQuery] = useQuery();

  const {
    data: latestShipperRating,
    isInitialLoading: isLoadingLatestShipperRating,
  } = useLatestShipperRating(shipperProfile.guid);

  const hasLatestShipperRating = !!latestShipperRating?.guid;

  const isRatingAvailable = useIsRatingAvailable(
    shipperProfile.last_order_completed_date,
  );

  return (
    <RatingContainer shouldCollapse={hasLatestShipperRating}>
      <Inline space="xxsmall" verticalAlign="center" noWrap={true}>
        <ThumbUp fontSize="small" color="action" />

        <div>
          <Link
            aria-label="rating percentage"
            component="button"
            onClick={() => {
              if (tab !== 'ratings') {
                setQuery(
                  {
                    tab: 'ratings',
                    utm_source: chooseUTMSource(),
                    utm_medium: 'Shipper Profile',
                    utm_content: 'Overall Rating',
                  },
                  true,
                );
              }
            }}
          >
            <Typography variant="h3">
              {shipperProfile.rating_details?.overall_rating || 0}%
            </Typography>
          </Link>
          &nbsp;
          <Typography
            aria-label="ratings count"
            color="textSecondary"
            component="span"
          >
            (
            {formatNumber(
              shipperProfile.rating_details?.total_rating_count || 0,
            )}
            )
          </Typography>
        </div>
      </Inline>

      {!isLoadingLatestShipperRating &&
        (hasLatestShipperRating ? (
          <EditRating
            shipperRating={latestShipperRating}
            lastOrderCompletedDate={shipperProfile.last_order_completed_date}
            onClickDeleteRating={onClickDeleteRating}
            onClickRate={onClickRate}
          />
        ) : isRatingAvailable ? (
          <Stack align="center" space="xxsmall">
            <Button onClick={onClickRate}>Rate Shipper</Button>
            <ShipperRatingAvailableTimeLeft
              date={shipperProfile.last_order_completed_date}
            />
          </Stack>
        ) : null)}
    </RatingContainer>
  );
}
